<template>
	<BasicButton color="secondary" uppercase :rounded="false" :filled="false" @click="clickHandler">
		<div class="filter-bar__reset-button">
			<span><FontAwesomeIcon :icon="faTimes" /></span>
			<span class="filter-bar__reset-button__text"> {{ $t('WEBAPP_RESET') }}</span>
		</div>
	</BasicButton>
</template>

<script setup lang="ts">
import type { FilterName } from '@/stores/useFilterStoreTypes';

import BasicButton from '@/ui-components/BasicButton.vue';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';

const props = withDefaults(
	defineProps<{
		filterNames?: FilterName[];
		updateRouteState?: boolean;
		keepProvidersState?: boolean;
		keepContentTypeState?: boolean;
	}>(),
	{
		filterNames: () => [],
		updateRouteState: true,
		keepProvidersState: false,
		keepContentTypeState: false,
	}
);

const emits = defineEmits(['resetClicked']);

const router = useRouter();

const filterStore = useFilterStore();

async function clickHandler() {
	// Note: Reset providers selection for SEO users, since we still use old Filters bar for SEO users
	emits('resetClicked');

	let newRoute;

	if (props.filterNames.length > 0) {
		newRoute = filterStore.resetFilters({
			filterNames: props.filterNames,
			updateRouteState: props.updateRouteState,
		});
	} else {
		newRoute = await filterStore.reset({
			keepProvidersState: props.keepProvidersState,
			keepContentTypeState: props.keepContentTypeState,
		});
	}

	if (newRoute) {
		router.push(newRoute).catch((error: any) => {
			if (
				!isNavigationFailure(error, NavigationFailureType.duplicated) &&
				!isNavigationFailure(error, NavigationFailureType.cancelled)
				// a reset of the filters sometimes triggers a navigation to the same page
			) {
				throw error;
			}
		});
	}
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.filter-bar {
	&__reset-button {
		display: flex;
		align-items: center;

		& span {
			display: flex;
		}

		&__text {
			font-weight: bold;
			margin-inline-start: 4px;

			@media (max-width: $screen-xs) {
				display: none !important;
			}
		}
	}
}
</style>
